import type { FunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Heading from '@atlaskit/heading';
import EmptyState from '@atlaskit/empty-state';
import Spinner from '@atlaskit/spinner';
import Flag, { FlagGroup } from '@atlaskit/flag';
import InfoIcon from '@atlaskit/icon/core/information';
import SectionMessage from '@atlaskit/section-message';
import Link from '@atlaskit/link';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import SearchFailedImage from '#assets/search-failed.svg';
import { useMcpContext } from '../McpContextProvider';
import { useSelectedWorkspace } from '../../../Flow/ConsentContextProvider';
import { useMcpBranding } from '../mcpTerminology';
import {
  mcpBannerMessages,
  mcpPermissionsMessages,
} from '../../MCP/McpPermissions/messages';
import McpIntentPermissionCard from '../../MCP/McpPermissions/McpIntentPermissionCard';
import { useUnselectedPermissionsWarning } from '../../MCP/McpPermissions/useUnselectedPermissionsWarning';
import { MCP_PERMISSIONS_MANAGEMENT_DOC_URL as MCP_PERMISSIONS_MANAGEMENT_DOC } from '../../MCP/mcpUrls';
import {
  MCP_POLICY_STATUS,
  needsNoPermissionsEmptyState,
} from '../domain/policyState';
import { FIELD_MAX_WIDTH } from '../fieldMaxWidth';

// Same cap as the site/workspace selectors: `width: 100%` shrinks with the
// panel; `maxWidth` stops the list outgrowing the fields so they stay aligned.
const stackStyle = xcss({ width: '100%', maxWidth: FIELD_MAX_WIDTH });
const headerRowStyle = xcss({ minHeight: 'size.300' });

const flagContainerStyle = xcss({
  position: 'fixed',
  bottom: 'space.400',
  left: 'space.400',
  // @ts-expect-error static z-index
  zIndex: 100,
  width: '400px',
  maxWidth: '90vw',
  backgroundColor: 'color.background.warning',
  borderRadius: 'radius.small',
  boxShadow: 'elevation.shadow.overlay',
});

const McpPermissions: FunctionComponent = () => {
  const { selectedWorkspace } = useSelectedWorkspace();
  const {
    mcpPolicyStatus,
    mcpPolicyErrorKind,
    policies,
    mcpByIntent,
    mcpSelectedToolsetNames,
    toggleMcpToolset: toggleToolset,
    selectedTrelloWorkspaces,
    selectedBitbucketWorkspace,
    inboxPlannerScopesDisabled,
    isTrelloMultiWorkspaceSelectorEnabled,
    availableTrelloWorkspaces,
    hasTrelloInboxPlannerScopes,
    scopeClassification,
    hasOrgMismatch,
  } = useMcpContext();

  const { formatMessage } = useIntl();
  const { product, productUnit } = useMcpBranding();
  const [flagDismissed, setFlagDismissed] = useState(false);
  const selectedSet = mcpSelectedToolsetNames ?? new Set<string>();
  const hasUnselected = useUnselectedPermissionsWarning({
    selectedWorkspace,
    mcpByIntent,
    selectedToolsetNames: selectedSet,
  });

  useEffect(() => {
    setFlagDismissed(false);
  }, [selectedWorkspace]);

  // Inbox and Planner are account level, so a member with no workspaces at all
  // can still grant them — permissions stay visible without a selection.
  const isInboxPlannerOnly =
    scopeClassification.hasTrello &&
    availableTrelloWorkspaces.length === 0 &&
    hasTrelloInboxPlannerScopes;

  // Don't show permissions until the user has selected a workspace, either an
  // Atlassian site, a Trello workspace, or a Bitbucket workspace.
  if (
    !selectedWorkspace &&
    selectedTrelloWorkspaces.length === 0 &&
    !selectedBitbucketWorkspace &&
    !isInboxPlannerOnly
  )
    return null;

  // The site and Bitbucket selections are in different organizations, so
  // nothing here can be granted as it stands. Listing permissions the user
  // cannot accept — and letting them tick boxes — would read as progress, so
  // the list stays away until one of the two selections changes. Both
  // selectors say why, and Accept is blocked (see mcpAcceptDisabled).
  //
  // Ahead of every branch below, including the spinner and the empty states:
  // the toolsets have usually loaded for both sources, so this is not an
  // absence of data but a refusal to offer it.
  if (hasOrgMismatch) return null;

  // A failed check is shown full-panel by McpPolicyAlerts, which keys off the
  // same field — so this hides whenever that shows, and the two can never
  // compete. Checked before the spinner: a check still in flight elsewhere must
  // not replace an error that has already happened.
  if (mcpPolicyErrorKind) return null;

  // Keep permissions from sources that already resolved visible while another
  // independent source is still loading, rather than replacing them with a
  // skeleton.
  if (
    mcpByIntent.length === 0 &&
    (mcpPolicyStatus === MCP_POLICY_STATUS.loading ||
      mcpPolicyStatus === MCP_POLICY_STATUS.idle)
  ) {
    return (
      <Stack xcss={stackStyle} space="space.400">
        <Inline alignBlock="center" xcss={headerRowStyle}>
          <Heading size="small">
            <FormattedMessage {...mcpPermissionsMessages.sectionTitle} />
          </Heading>
        </Inline>
        <Spinner size="large" label="Loading permissions" />
      </Stack>
    );
  }

  if (mcpByIntent.length === 0) {
    // The site and Bitbucket selectors report an empty result inline, so the
    // panel-level empty state would just say it again.
    if (!needsNoPermissionsEmptyState(policies)) {
      return null;
    }

    // Unwrapped: EmptyState caps itself at 464px and centres with auto margins,
    // so a 550px box around it cannot clamp anything — it would only re-centre
    // the block in a narrower box. Left as the default flow renders it.
    return (
      <EmptyState
        header={formatMessage(
          isTrelloMultiWorkspaceSelectorEnabled
            ? mcpPermissionsMessages.emptyStateHeadingMultiple
            : mcpPermissionsMessages.emptyStateHeading,
          { productUnit },
        )}
        description={
          <FormattedMessage
            {...mcpPermissionsMessages.emptyStateDescription}
            values={{
              howPermissionsLink: (text) => (
                <Link
                  href={MCP_PERMISSIONS_MANAGEMENT_DOC}
                  target="_blank"
                  rel="noreferrer"
                >
                  {text}
                </Link>
              ),
            }}
          />
        }
        imageUrl={SearchFailedImage}
      />
    );
  }

  return (
    <Stack xcss={stackStyle} space="space.100">
      {isInboxPlannerOnly && (
        <SectionMessage appearance="information">
          <Text as="p">
            <FormattedMessage
              {...mcpBannerMessages.noWorkspacesInboxPlannerMessage}
            />
          </Text>
        </SectionMessage>
      )}
      <Inline
        alignBlock="center"
        spread="space-between"
        space="space.200"
        shouldWrap
        xcss={headerRowStyle}
      >
        <Heading size="small">
          <FormattedMessage {...mcpPermissionsMessages.sectionTitle} />
        </Heading>
      </Inline>
      {hasUnselected && !flagDismissed && (
        <Box xcss={flagContainerStyle}>
          <FlagGroup onDismissed={() => setFlagDismissed(true)}>
            <Flag
              id="mcp-permission-warning"
              icon={
                <InfoIcon label="" color={token('color.icon.information')} />
              }
              title={formatMessage(mcpBannerMessages.editWarningTitle, {
                product,
              })}
              description={formatMessage(mcpBannerMessages.editWarningBody, {
                product,
              })}
              actions={[
                {
                  content: formatMessage(mcpBannerMessages.editWarningDismiss),
                  href: 'javascript:void(0)',
                  onClick: (e) => {
                    e.preventDefault();
                    setFlagDismissed(true);
                  },
                },
              ]}
            />
          </FlagGroup>
        </Box>
      )}
      <Stack space="space.200">
        {mcpByIntent.map((group) => (
          <McpIntentPermissionCard
            key={group.id}
            group={group}
            selectedToolsetNames={selectedSet}
            onToggleToolset={(name, checked) => toggleToolset(name, checked)}
            onToggleSummaryCard={(checked) =>
              group.toolsets.forEach((t) => toggleToolset(t.name, checked))
            }
          />
        ))}
      </Stack>
      {inboxPlannerScopesDisabled && (
        <SectionMessage appearance="information">
          <Text as="p">
            <FormattedMessage
              {...(isTrelloMultiWorkspaceSelectorEnabled
                ? mcpBannerMessages.inboxPlannerDisabledMultipleMessage
                : mcpBannerMessages.inboxPlannerDisabledMessage)}
            />
          </Text>
        </SectionMessage>
      )}
    </Stack>
  );
};

export default McpPermissions;
